/**
 * Auth Redirect URL Builder
 *
 * Builds redirect URLs dynamically from current origin.
 * NEVER hardcodes domain names.
 *
 * Usage:
 * - buildCallbackUrl() → https://example.com/auth/callback
 * - buildResetPasswordUrl() → https://example.com/auth/reset-password
 */

/**
 * Get the current origin, with optional override from environment
 */
export function getOrigin(): string {
  // Canonical public URL (recommended). Set in Vercel env for production/preview.
  const envOverride = import.meta.env.VITE_PUBLIC_SITE_URL;
  if (envOverride) return envOverride.replace(/\/$/, '');

  if (typeof window !== 'undefined') {
    // In production, password reset links must land on a domain allowlisted in Supabase Auth.
    // If reset links ever fail, set VITE_PUBLIC_SITE_URL.
    return window.location.origin;
  }

  throw new Error('Cannot determine origin: no window and no VITE_PUBLIC_SITE_URL');
}

/**
 * Build the auth callback URL for PKCE flow
 */
export function buildCallbackUrl(): string {
  return `${getOrigin()}/auth/callback`;
}

/**
 * Build the password reset URL for recovery emails
 */
export function buildResetPasswordUrl(): string {
  return `${getOrigin()}/auth/reset-password`;
}

/**
 * Build the login URL
 */
export function buildLoginUrl(): string {
  return `${getOrigin()}/login`;
}

/**
 * Build URL with return path for post-auth redirect
 */
export function buildCallbackUrlWithReturn(returnPath?: string): string {
  const callbackUrl = buildCallbackUrl();
  if (returnPath) {
    return `${callbackUrl}?returnTo=${encodeURIComponent(returnPath)}`;
  }
  return callbackUrl;
}

/**
 * Validate a cross-product `next` redirect target (central-login SSO).
 *
 * Only RadiologyLive origins may be redirected to — anything else is an
 * open-redirect vector and returns null. Relative paths are allowed (same app).
 */
export function getValidatedNextUrl(raw: string | null): string | null {
  if (!raw) return null;
  if (raw.startsWith('/') && !raw.startsWith('//')) return raw;
  try {
    const url = new URL(raw);
    if (url.protocol !== 'https:' && url.protocol !== 'http:') return null;
    const host = url.hostname;
    const allowed =
      host === 'radiologylive.ai' ||
      host.endsWith('.radiologylive.ai') ||
      host === 'localhost';
    if (url.protocol === 'http:' && host !== 'localhost') return null;
    return allowed ? url.toString() : null;
  } catch {
    return null;
  }
}

/**
 * Strip auth params from URL (for cleaning up after callback)
 */
export function cleanAuthParamsFromUrl(): void {
  if (typeof window === 'undefined') return;

  const url = new URL(window.location.href);
  const hasAuthParams =
    url.searchParams.has('code') ||
    url.searchParams.has('error') ||
    url.searchParams.has('error_description') ||
    url.hash.includes('access_token') ||
    url.hash.includes('refresh_token');

  if (hasAuthParams) {
    // Remove auth-related query params
    url.searchParams.delete('code');
    url.searchParams.delete('error');
    url.searchParams.delete('error_description');

    // Clear hash if it contains tokens
    if (url.hash.includes('access_token') || url.hash.includes('refresh_token')) {
      url.hash = '';
    }

    window.history.replaceState({}, document.title, url.pathname + url.search);
  }
}

/**
 * Check if current URL has auth callback params
 */
export function hasAuthCallbackParams(): boolean {
  if (typeof window === 'undefined') return false;

  const url = new URL(window.location.href);
  return (
    url.searchParams.has('code') ||
    url.hash.includes('access_token') ||
    url.hash.includes('type=recovery')
  );
}

/**
 * Check if current URL has recovery/reset params
 */
export function hasRecoveryParams(): boolean {
  if (typeof window === 'undefined') return false;

  const url = new URL(window.location.href);
  const hash = url.hash;

  return (
    hash.includes('type=recovery') ||
    hash.includes('type=signup') ||
    url.searchParams.has('type') && url.searchParams.get('type') === 'recovery'
  );
}
